import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import  {axiosInstance}  from '../../axiosConfig';
import { useSelector } from 'react-redux';
import "../../App.css";
import { ExportButton } from './StrategyContent';
import LoadingChart from './LoadingChart';
import { Box, Typography } from '@mui/material';

function escapeString(str) {
    return str.replace(/['[\]&]/g, '\\$&');
}

function wrapText(textElement, maxChars = 35) {
    const text = d3.select(textElement);
    if (!text.node()) return;

    const tspan = text.select('tspan');
    const fullText = tspan.empty() ? text.text() : tspan.text();
    if (!fullText) return;

    const originalX = text.attr("x") || 0;
    const originalDy = text.attr("dy") || 0;

    const words = fullText.split(' ');
    text.selectAll("tspan").remove();
    text.text('');

    let line = '';
    let lineCount = 0;

    words.forEach((word) => {
        if ((line + word).length > maxChars) {
            text.append('tspan')
                .attr('x', originalX)
                .attr('dy', lineCount === 0 ? originalDy : '1.1em')
                .style("font-size", "12px")
                .style("font-family", "helvetica-neue, helvetica, sans-serif")
                .text(line.trim());
            line = '';
            lineCount++;
        }
        line += word + ' ';
    });

    if (line.trim()) {
        text.append('tspan')
            .attr('x', originalX)
            .attr('dy', lineCount === 0 ? originalDy : '1.1em')
            .style("font-size", "12px")
            .style("font-family", "helvetica-neue, helvetica, sans-serif")
            .text(line.trim());
    }
}

// Extract min and max correlation values from the tree structure
function getCorrelationRange(node) {
    const correlations = [];

    function traverse(n) {
        if (n?.correlation !== undefined && n.correlation !== null) {
            correlations.push(n.correlation);
        }
        if (n?.children) {
            n.children.forEach(child => traverse(child));
        }
    }

    traverse(node);

    if (correlations.length === 0) {
        return { min: -1, max: 1 }; // Default range
    }

    return {
        min: Math.min(...correlations),
        max: Math.max(...correlations)
    };
}


// Create a global mapping between nodes and their data for tooltip lookup
let nodeDataMapping = new Map();

// Helper function to create tooltip content for nodes (circles)
function createNodeTooltipContent(nodeId, tickerFromAnno, assets, fallbackText = 'Node') {
    const nodeData = nodeDataMapping.get(nodeId);

    let tooltipContent = '';
    if (nodeData) {
        const { correlation } = nodeData;

        // Show only correlation value for nodes
        if (typeof correlation === 'number') {
            tooltipContent = `Correlation: ${correlation.toFixed(4)}`;
        } else {
            tooltipContent = 'No correlation data';
        }
    } else {
        // Fallback if mapping fails
        tooltipContent = 'No data available';
    }

    return tooltipContent;
}

// Helper function to create tooltip content for labels (asset names)
function createLabelTooltipContent(nodeId, tickerFromAnno, assets, fullText) {
    const nodeData = nodeDataMapping.get(nodeId);

    let tooltipContent = '';
    if (nodeData) {
        const { ticker, displayName, correlation } = nodeData;

        // Show asset name and correlation for labels
        if (ticker) {
            const matchedAsset = assets.find(a => a.ticker === ticker);
            tooltipContent = matchedAsset
                ? `${matchedAsset.ticker}: ${matchedAsset.description}`
                : `${ticker}: ${displayName}`;
        } else if (displayName) {
            tooltipContent = displayName;
        } else {
            tooltipContent = fullText || 'Unknown asset';
        }

        if (typeof correlation === 'number') {
            tooltipContent += ` | Correlation: ${correlation.toFixed(4)}`;
        }
    } else {
        // Fallback if mapping fails
        tooltipContent = fullText || 'No data available';
    }

    return tooltipContent;
}

function convertToNewickString(node, assets) {
    // Display label is the human-readable fund name, fallback to ticker
    let displayName = node?.name || node?.ticker || ''; // Changed to let
    const distance = node?.distance || 0;

    // --- FIX STARTS HERE ---
    // According to the Newick format, labels with special characters (parentheses, commas, etc.)
    // must be quoted to be parsed correctly.
    if (/[()[\]{},;:]/.test(displayName)) {
        // If the name itself contains a single quote, it must be escaped by doubling it (e.g., 'O'Malley' becomes 'O''Malley').
        displayName = `'${displayName.replace(/'/g, "''")}'`;
    }
    // --- FIX ENDS HERE ---

    // Match asset by ticker when available for robust metadata
    const matchedAsset = assets.find(asset => asset.ticker === (node?.ticker || ''))
        || assets.find(asset => asset.description === (node?.name || '')); // Use original name for matching
    const description = matchedAsset ? escapeString(matchedAsset.description || '') : '';
    const ticker = matchedAsset?.ticker || node?.ticker || '';

    // Create a unique identifier for this node
    const nodeId = ticker || displayName || `cluster_${Math.random().toString(36).substr(2, 9)}`;

    // Store the original node data for tooltip lookup
    nodeDataMapping.set(nodeId, {
        originalNode: node,
        ticker: ticker,
        displayName: displayName,
        description: description,
        correlation: node?.correlation,
        nAssets: node?.n_assets_in_cluster
    });

    // Embed both description and ticker as annotations so figtree can carry them along
    const parts = [];
    if (description) parts.push(`description='${description}'`);
    if (ticker) parts.push(`ticker='${ticker}'`);
    parts.push(`nodeId='${nodeId}'`); // Add nodeId for lookup
    const annotations = parts.length ? `[&${parts.join(',')}]` : '';

    // Use the (now correctly quoted, if necessary) displayName
    const label = `${displayName}${annotations}`;

    if (!node?.children || node.children.length === 0) {
        return `${label}:${distance}`;
    }

    const childrenStrings = node.children.map(child => convertToNewickString(child, assets)).join(',');
    let nodeString = `(${childrenStrings})${label}`;

    if (node.distance !== undefined) {
        nodeString += `:${node.distance}`;
    }

    return nodeString;
}


const PhylogeneticTree = ({ strategyId }) => {
    const svgRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    const [newickTree, setNewickTree] = useState(null);
    const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const assets = useSelector(state => state.strategy.assets);
    const settings = useSelector(state => state.strategy.settings);
    const calculated = useSelector(state => state.strategy.calculated);

    useEffect(() => {
        const fetchTreeData = async () => {
            // Only fetch data after user clicks "Calculate"
            if (!calculated || !settings.startDate || !settings.endDate || !settings.currency) {
                return;
            }

            setError(null); // Reset error state
            setIsLoading(true);
            try {
                const response = await axiosInstance.get(
                    `/strategies/${strategyId}/cladogram/`, {
                        params: {
                            start_date: settings.startDate,
                            end_date: settings.endDate,
                            currency: settings.currency
                        }
                    }
                );

                const cladogram = response.data.cladogram;
                setNewickTree(cladogram);
                setIsReady(true);
            } catch (error) {
                console.error('Error fetching tree data:', error);
                setError('Failed to load cladogram data. The selected date range may not have complete data. Please try different dates.');
                setIsReady(false);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTreeData();
    }, [strategyId, calculated, settings]);

    useEffect(() => {
        const loadFigTree = async () => {
            if (!isReady || !svgRef.current || !newickTree) return;

            try {
                const figtreeModule = await import('https://rambaut.github.io/figtree.js/dist/figtree.esm.js');
                const { Tree, FigTree, rectangularLayout, branch, circle, tipLabel, internalNodeLabel } = figtreeModule;

                // Clear previous mapping
                nodeDataMapping.clear();
                const newickString = convertToNewickString(newickTree, assets) + ';';

                const tree = Tree.parseNewick(newickString);
                tree.annotateNode(tree.root, { root: true });

                                // Increase margins significantly for better spacing and label visibility
                const margins = { top: 20, bottom: 80, left: 120, right: 350 };
                const branchSettings = branch().hilightOnHover().reRootOnClick().curve(d3.curveStepBefore);

                const figTree = new FigTree(svgRef.current, margins, tree)
                    .layout(rectangularLayout)
                    .nodes(
                        circle().attr("r", 5).hilightOnHover(10).rotateOnClick(),
                        tipLabel(d => d.name), // Keep original tipLabel
                        internalNodeLabel(d => d.label)
                    )
                    .nodeBackgrounds(circle().attr("r", 7))
                    .branches(branchSettings);

                figTree.update();

                // Draw correlation scale (0 to 1) aligned to figtree margins
                const svg = d3.select(svgRef.current);
                const bbox = svgRef.current.getBoundingClientRect();
                const width = Math.max(bbox.width || 1000, 1000); // Ensure minimum width for proper spacing
                // Count assets from the cladogram tree structure instead of Redux store
                const countAssetsInTree = (node) => {
                    if (!node.children || node.children.length === 0) {
                        return 1; // Leaf node
                    }
                    return node.children.reduce((sum, child) => sum + countAssetsInTree(child), 0);
                };

                const assetCount = countAssetsInTree(newickTree);
                const height = bbox.height || (assetCount > 10 ? 850 : Math.max(600, assetCount * 60)); // Dynamic height based on asset count

                // Get actual correlation range from the response data
                const correlationRange = getCorrelationRange(newickTree);

                // purge previous axis if any
                svg.selectAll('.distance-axis').remove();

                // Use actual min from data, but always extend max to 1
                const minDomain = correlationRange.min;
                const maxDomain = Math.max(1, correlationRange.max);

                const corrScale = d3.scaleLinear()
                    .domain([minDomain, maxDomain])
                    .range([margins.left, width - margins.right]);

                // Move axis slightly lower to avoid collision with asset labels
                const axisYOffset = 20; // pixels pushed downwards
                const axisY = height - (margins.bottom - axisYOffset);
                const axisG = svg.append('g')
                    .attr('class', 'distance-axis')
                    .attr('transform', `translate(0, ${axisY})`);

                axisG.call(d3.axisBottom(corrScale).ticks(6).tickFormat(d3.format('.2f')));

                axisG.append('text')
                    .attr('x', width - margins.right)
                    .attr('y', 35)
                    .style('font-size', '12px')
                    .style('fill', '#666')
                    .style('text-anchor', 'end')
                    .text('Correlation');

                // Post-process elements to add wrapping and accurate tooltips
                setTimeout(() => {
                    const svg = d3.select(svgRef.current);

                    // Add tooltips to circle nodes (black dots)
                    const circleElements = svg.selectAll('g.node circle');
                    circleElements
                        .style('cursor', 'pointer')
                        .on('mouseover', function(event) {
                            const nodeDatum = d3.select(this.parentNode).datum() || {};
                            const nodeId = nodeDatum?.annotations?.nodeId;
                            const tickerFromAnno = nodeDatum?.annotations?.ticker;

                            const tooltipContent = createNodeTooltipContent(nodeId, tickerFromAnno, assets, 'Node');

                            const rect = event.target.getBoundingClientRect();
                            setTooltip({
                                show: true,
                                content: tooltipContent,
                                x: rect.left - 10,
                                y: rect.top - 30
                            });
                        })
                        .on('mouseout', function() {
                            setTooltip({ show: false });
                        });

                    // Limit strictly to node labels to avoid catching axis texts
                    const textElements = svg.selectAll('g.node text');

                    textElements.each(function() {
                        const text = d3.select(this);
                        const textContent = text.text();
                        if (textContent && (textContent.includes('.') || textContent.length > 10)) {
                            wrapText(this);
                        }
                    });

                    textElements
                        .style('cursor', 'pointer')
                        .style('font-size', '12px')
                        .style('font-family', 'helvetica-neue, helvetica, sans-serif')
                        .on('mouseover', function(event) {
                            const nodeDatum = d3.select(this.parentNode).datum() || {};
                            const nodeId = nodeDatum?.annotations?.nodeId;
                            const tickerFromAnno = nodeDatum?.annotations?.ticker;
                            const fullText = d3.select(this).text();

                            const tooltipContent = createLabelTooltipContent(nodeId, tickerFromAnno, assets, fullText);

                            const rect = event.target.getBoundingClientRect();
                            setTooltip({
                                show: true,
                                content: tooltipContent,
                                x: rect.left - 10,
                                y: rect.top - 30
                            });
                        })
                        .on('mouseout', function() {
                            setTooltip({ show: false });
                        });
                }, 100); // Small delay to ensure elements are rendered
            } catch (error) {
                console.error('Error loading or initializing FigTree:', error);
            }
        };

        loadFigTree();
    }, [isReady, newickTree, assets]);


    return (
        <Box className="phylogenetic-tree-container" sx={{ position: 'relative', width: '100%' }} id="phylogramDownload">
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '10px 0',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 100,
                borderBottom: '1px solid #e0e0e0'
            }}>
                <ExportButton chartId="phylogramDownload" />
            </Box>
            {isLoading ? (
                <LoadingChart message="Loading cladogram data..." />
            ) : error ? (
                <Box sx={{ textAlign: 'center', padding: '40px' }}>
                    <Typography color="error" variant="h6" gutterBottom>
                        Cladogram Unavailable
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {error}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{
                    width: '100%',
                    overflow: 'auto',
                    minHeight: '500px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                }}>
                    <svg
                        ref={svgRef}
                        id="phylogram_1a"
                        className='cladogram-wrapper'
                        style={{
                            width: '100%',
                            minWidth: '1000px',
                            minHeight: `${newickTree ? (() => {
                                const countAssetsInTree = (node) => {
                                    if (!node.children || node.children.length === 0) {
                                        return 1; // Leaf node
                                    }
                                    return node.children.reduce((sum, child) => sum + countAssetsInTree(child), 0);
                                };
                                const assetCount = countAssetsInTree(newickTree);
                                return assetCount > 10 ? 1200 : Math.max(500, assetCount * 60);
                            })() : 500}px`,
                            height: 'auto',
                            display: 'block'
                        }}
                    ></svg>
                </Box>
            )}
            {tooltip.show && (
                <div
                    style={{
                        position: 'fixed',
                        left: `${tooltip.x}px`,
                        top: `${tooltip.y}px`,
                        background: '#333',
                        color: 'white',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontFamily: 'Arial, sans-serif',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        maxWidth: '300px',
                        wordWrap: 'break-word',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        border: '1px solid #666',
                        transform: 'translateX(-100%)', // Move tooltip to the left of cursor
                    }}
                >
                    {tooltip.content}
                </div>
            )}
            <style>{`
                    .phylogenetic-tree-container {
            font-family: "helvetica-neue", "helvetica", "sans-serif";
            position: relative;
            width: 100%;
            min-height: 500px;
          }

          .cladogram-wrapper {
            width: 100%;
            height: auto;
            background-color: #fafafa;
            display: block;
          }

          /* Responsive improvements */
          @media (max-width: 768px) {
            .cladogram-wrapper {
              min-width: 800px !important;
            }
          }
        `}</style>
        </Box>
    );
};

export default PhylogeneticTree;
